import React, { Fragment } from 'react';
import { Box, Checkbox, IconButton, Stack, MenuItem, Grid, FormControl, Radio, RadioGroup, Accordion, AccordionSummary, AccordionDetails, Typography, FormLabel, TablePagination, InputAdornment, Divider } from '@mui/material';
import { StyledCard, StyledCardTitle, CustomDatePicker, InputProps, CustomButton, CustomSelectRounded, CustomInputLabel, SelectInputProps, FlexDiv, CustomRadio, TableBadge, CustomCheckbox, CustomSearch } from '../../../../../custom/components';
import approvalStyles from '../../approval.module.css';
import styles from '../../../../../custom/style.module.css';
import moment from 'moment';
import dayjs from 'dayjs';
import { AttachFile, Comment, ExpandMore, OfflinePin, Search } from '@mui/icons-material';
import ReviewModal from '../../../common/ReviewModal';
import FlightReportModal from '../../../common/FlightReportModal';
import cookies from 'utils/cookies';
import { BinoIcon, DateFormat, StickyNoteIcon } from 'custom/constants';
import AddNotes from './addnotes';
import TableWrapper from 'components/pages/common/TanStackTableWrapper';

const STACKFILTER = {
  direction:{
    xs: 'column',
    lg: 'row',
  }, 
  align:{
    xs: 'flex-start',
    lg: 'center',
  }
};

const getFlightNo = (item) => {
  if (!item || !item.flight || !item.flight.airline) return '';
  return `${item.flight.airline.code}${item.flight.flight_no}`;
};

const getFlightDate = (item) => {
  if (!item || !item.flight || !item.flight.flight_date) return '';
  return `${moment(item.flight.flight_date).format(DateFormat).toUpperCase()}`;
};

function formatNumber(num) {
  return num % 1 === 0 ? Math.round(num) : num.toFixed(1);
}

const PopoverActionCell = ({ row }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState([]);
  const [showMoreNote, setShowMoreNote] = React.useState({});

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row?.notes || []);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow([]);
  };

  const handleReadMore = (index) => {
    setShowMoreNote((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <>
      <TableBadge badgeContent={row?.notes?.length} color='error' overlap='circular' 
        classes={{ 
          root: approvalStyles.billingStatsNotesBadgeRoot,
          badge: approvalStyles.billingStatsNotesBadge
        }}
      >
        <StickyNoteIcon title='View Notes' onClick={handleClick} />
      </TableBadge>
      <AddNotes
        anchorEl={anchorEl}
        popup={anchorEl ? 'notes' : false}
        closePopup={handleClose}
        notes={selectedRow}
        handleReadMore={handleReadMore}
        showMoreNote={showMoreNote}
        isRemarks={false}
        hideInput={true}
      />
    </>
  );
};

const FlightReport = (props) => {
  const col = [
    {
      id: 'flight_info',
      header: (<Typography color='transparent'>-</Typography>),
      columns: [
        {
          id: 'status',
          accessorKey: 'status',
          header: (<Typography color='transparent'>-</Typography>),
          cell: ({ row }) => {
            let reportStatusClassName = '';
            switch (row.original.status) {
              case 'Pending':
                reportStatusClassName = props.showCompleted ? styles.approved : 'pending';
                break;
              case 'Rejected':
                reportStatusClassName = 'rejected';
                break;
              case 'Approved':
                reportStatusClassName = 'approved';
                break;
              default:
                reportStatusClassName = 'pending';
            }
            return <span className={`${styles.dotLarge} ${styles[reportStatusClassName]} ${styles.mt1}`} />;
          },
        },
        {
          id: 'checkbox',
          accessorKey: 'checkbox',
          enableSorting: false,
          header: () => {
            return <Checkbox
              checked={props.allSelected}
              onChange={(e) => props.selectAll(e.target.checked)}
              inputProps={{ 'aria-label': 'Select' }}
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 18 },
                color: 'white',
                p: 0,
                '&.Mui-checked': { color: 'white' },
              }}
              name='approval'
              disabled={props.showCompleted}
            />;
          },
          cell: ({ row }) => {
            return <Checkbox
              onClick={(e) => props.selectFlights(row.original.id, e.target.checked)}
              inputProps={{ 'aria-label': 'Select' }}
              sx={{
                '& .MuiSvgIcon-root': { fontSize: 18 },
                p: 0,
              }}
              name='approval'
              checked={props.ids.includes(row.original.id)}
              disabled={props.showCompleted}
            />;
          },
        },
        {
          id: 'flight',
          accessorKey: 'flight',
          accessorFn: (row) => getFlightNo(row),
          header: 'Flight',
          cell: (info) => info.getValue(),
        },
        {
          id: 'date',
          accessorFn: (row) => getFlightDate(row) || '',
          header: 'Date',
          cell: (info) => info.getValue(),
          sortingFn: 'datetime', 
        },
        {
          id: 'org',
          header: 'O_D',
          accessorFn: (row) => row.flight?.org || '',
          cell: ({ row }) => {
            return `${row.original.flight.org} - ${row.original.flight.dest}`;
          } 
        },
        {
          id: 'io',
          accessorKey: 'io',
          accessorFn: (row) => row.flight.org === cookies.get('station_code') ? 'Outbound' : 'Inbound',
          header: 'I/O',
          cell: (info) => info.getValue(),
        },
      ]
    },
    {
      id: 'awb_info',
      header: () =><RadioGroup
        row
        aria-label='radio group'
        onChange={props.handleRadioChange}
        name='entity'
        defaultValue='total'
        value={props.awbType}
      >
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-around' width={'100%'}>
          <CustomRadio
            control={
              <Radio
                value='loose'
                size='small'
                classes={{
                  root: approvalStyles.filterCheckboxRoot,
                  checked: approvalStyles.filterCheckedCheckbox
                }}
              />
            }
            label='Loose'
            classes={{
              root: approvalStyles.checkboxLabelRoot,
              label: approvalStyles.checkboxLabel
            }}
          />
          <CustomRadio
            control={
              <Radio
                value='intact'
                size='small'
                classes={{
                  root: approvalStyles.filterCheckboxRoot,
                  checked: approvalStyles.filterCheckedCheckbox
                }}
              />
            }
            label='Intact'
            classes={{
              root: approvalStyles.checkboxLabelRoot,
              label: approvalStyles.checkboxLabel
            }}
          />
          <CustomRadio
            control={
              <Radio
                value='total'
                size='small'
                classes={{
                  root: approvalStyles.filterCheckboxRoot,
                  checked: approvalStyles.filterCheckedCheckbox
                }}
              />
            }
            label='Total'
            classes={{
              root: approvalStyles.checkboxLabelRoot,
              label: approvalStyles.checkboxLabel
            }}
          />
        </Stack>
      </RadioGroup>,
      columns: [
        {
          id: 'awb',
          header: () => <div className={styles.textCenter}>Awb</div>,
          accessorFn: (row) => row[`${props.awbType}_awb`] ?? '',
          cell: ({ row }) => row.original[`${props.awbType}_awb`] ?? '',
        },
        {
          id: 'pcs',
          header: () => <div className={styles.textCenter}>Pcs</div>,
          accessorFn: (row) => row[`${props.awbType}_pcs`] ?? '',
          cell: ({ row }) => row.original[`${props.awbType}_pcs`] ?? '',
        },
        {
          id: 'weight',
          header: () => <div className={styles.textCenter}>Wgt</div>,
          accessorFn: (row) => formatNumber(row[`${props.awbType}_weight`] ?? ''),
          cell: ({ row }) => formatNumber(row.original[`${props.awbType}_weight`] ?? ''),
        },
        {
          id: 'ld3_count',
          header: () => <div className={styles.textCenter}>LD3</div>,
          accessorFn: (row) => row[`${props.awbType}_LD3_count`] ?? '',
          cell: ({ row }) => row.original[`${props.awbType}_LD3_count`] ?? '',
        },
        {
          id: 'ld7_count',
          header: () => <div className={styles.textCenter}>LD7</div>,
          accessorFn: (row) => row[`${props.awbType}_LD7_count`] ?? '',
          cell: ({ row }) => row.original[`${props.awbType}_LD7_count`] ?? '',
        },
        {
          id: 'md_count',
          header: () => <div className={styles.textCenter}>MD</div>,
          accessorFn: (row) => row[`${props.awbType}_MD_count`] ?? '',
          cell: ({ row }) => row.original[`${props.awbType}_MD_count`] ?? '',
        },
      ]
    },
    {
      id: 'info',
      header: (<Typography color='transparent'>-</Typography>),
      columns: [
        {
          id: 'item_description',
          accessorKey: 'item_description',
          header: () => <div className={approvalStyles.itemDescTitle}>Item Description</div>,
          enableSorting: false,
          cell: ({ row }) => {
            return <Accordion 
              className={approvalStyles.rowAccordion} 
              classes={{ expanded: approvalStyles.accordionExpanded }}
              expanded={props.expandedRows.includes(row.original.id)}>
              <AccordionSummary 
                className={approvalStyles.rowAccordionSummary}
                expandIcon={<ExpandMore />}
                aria-controls='panel1-content'
                id='panel1-header'
                classes={{
                  expanded: approvalStyles.accordionExpanded,
                  content: approvalStyles.accordionContent,
                }}
                onClick={() =>  {
                  !row?.original?.flight?.contract_req?  props.fetchDesc(row.original.id) : null;
                  props.setExpandedRows(row.original.id);
                }}
              >
                <Typography variant='inherit' fontStyle='italic'>Expand to view Billing Statistics</Typography>
              </AccordionSummary>
              <AccordionDetails className={approvalStyles.accordionDetails}>
                {!row?.original?.flight?.contract_req && (
                  <Typography variant='inherit' fontStyle='italic' className={approvalStyles.loadingText}>Loading Billing Statistics...</Typography>
                )}
                <Stack spacing={1} component='ul' className={approvalStyles.accordionContentList}>
                  {row?.original?.flight?.contract_req && row?.original?.flight?.contracts && row?.original?.flight?.contracts[props.contract_Type]?.map((contract, i) => {
                    if (contract) {
                      return contract?.contractrows?.map((r, j) => {
                        // Find the matching report for this row, based on contractrowId
                        let matchingReport = row.original.report?.find((report) => {
                          return report.contractrowId === r.id;
                        });
                        let reportQty = matchingReport ? matchingReport.quantity : 0;
                        let unit = '';
                        if( r.dis_quantity ){
                          reportQty = matchingReport ? parseFloat(matchingReport.quantity) + parseFloat(r.dis_quantity) : parseFloat(0);  
                        }
                        reportQty = reportQty ?  reportQty : r.quantity + r.dis_quantity;
                        if(r.applied_on) {
                          if(r.applied_on.toLowerCase().includes('kgs') || r.applied_on.toLowerCase().includes('uld kgs')) {
                            unit = 'kg';
                          } else if(r.applied_on.toLowerCase().includes('pcs')) {
                            unit = 'pc';
                          } else if(r.applied_on.toLowerCase().includes('units') || r.applied_on.toLowerCase().includes('rent')) {
                            unit = 'unit';
                          } else if(r.applied_on.toLowerCase().includes('ulds')) {
                            unit = 'uld';
                          } else if(r.applied_on.toLowerCase().includes('flight')) {
                            unit = 'flight';
                          } else if(r.applied_on.toLowerCase().includes('air waybill')) {
                            unit = 'awb';
                          } else {
                            unit = '';
                          }
                        }
                        return (
                          <Box component='li' key={`${i}-${j}`}> 
                            <Grid container columnSpacing={1} rowSpacing={1} justifyContent='space-between' alignItems='flex-start' className={approvalStyles.billingStatsRow}>
                              <Grid item xs md width={200} whiteSpace={'wrap'}>
                                <Typography variant='inherit' textAlign={'left'} className={approvalStyles.billingStatsDescription}>{r.description}</Typography>
                              </Grid>
                              <Grid item xs md='auto' minWidth={80}>
                                <Typography variant='inherit' textAlign={'right'} fontWeight={600} className={approvalStyles.billingStatsQuantity}>{Number(reportQty).toFixed(2)}</Typography>
                              </Grid>
                              <Grid item xs md='auto' minWidth={40}>
                                <Typography variant='inherit' textAlign={'right'} className={approvalStyles.billingStatsUnit}>{unit}</Typography>
                              </Grid>
                              <Grid item xs='auto' className={approvalStyles.billingStatsNotes}>
                                <PopoverActionCell row={r} showMoreNote={props.showMoreNote} />
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      });
                    }}
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>;
          } 

        },
        {
          id: 'action',
          accessorKey: 'action',
          header: 'Action',
          enableSorting: false,
          cell: ({ row }) => {
            const latestRemarkLog = [...(row?.original?.log || [])].reverse().find(l => l.remark && String(l.remark).trim() !== '');
            const hasRemark = Boolean(latestRemarkLog?.remark);
            return (
              <Stack direction={'row'} spacing={1.5} alignItems={'center'} justifyContent={'center'}>
                <TableBadge badgeContent={row.original.flight?.docs?.length} color='error'>
                  <IconButton
                    disableRipple
                    onClick={() => props.handleOpen(row.original.flight?.docs, 'attached_docs')}
                    className={`${styles.p0}`}
                    size='small'
                    color='primary'
                  >
                    <AttachFile sx={{ fontSize: '1.1rem', }} />
                  </IconButton>
                </TableBadge>
                <IconButton
                  onClick={() => props.handleRemark('remark', latestRemarkLog?.remark)}
                  className={`${styles.p0}`}
                  size='small'
                  color='primary'
                  disabled={!hasRemark}
                >
                  <Comment fontSize='inherit' />
                </IconButton>
                <div className={styles.actionIcons}>
                  <BinoIcon title='View Flight Report' 
                    // className={`${styles.smActionIcons} ${flightInfoStyles.reportIcon}`}
                    onClick={() => props.handleFlight(row.original)} />
                </div>
                <IconButton onClick={(e) => props.handleReview(e.currentTarget, row.original)} className={`${styles.p0} ${styles.iconMid}`}
                  disabled={props.showCompleted}
                  size='small' color='primary'> 
                  <OfflinePin fontSize='18px' />
                </IconButton>
              </Stack>
            );
          } 
        },
      ]
    }
  ];

  return (
    <div>
      <div 
        className={`${styles.wAuto} ${approvalStyles.headerWraper}`}>
        <StyledCard className={`${approvalStyles.filterCard}`}>
          <FlexDiv className={`${approvalStyles.filterTitle}`}><StyledCardTitle>Flight Info</StyledCardTitle></FlexDiv>
          <Grid container columnSpacing={{
            xs: 4,
            lg: 2,
            xl: '2%'
          }} rowSpacing={{
            xs: 2,
            lg: 0,
          }} justifyContent={{
            xs: 'flex-start',
            xl: 'center',
          }}>
            <Grid item xs={12} sm={6} md={3} lg={2.5} xl={2.5}>
              <Stack direction={{
                xs: 'column',
                lg: 'row',
              }}
              align={{
                xs: 'flex-start',
                lg: 'center',
              }}>
                <CustomInputLabel id='airline-label' aria-label='Enter-airline' className={styles.ipLabel}>Airline</CustomInputLabel>
                <FormControl variant='standard' fullWidth>
                  <CustomSelectRounded
                    {...SelectInputProps}
                    id='airline'
                    labelId='airline-label'
                    fullWidth
                    autoFocus
                    name='airline'
                    className={`${styles.brInput}`}
                    onChange={props.handleAirlineChange}
                    value={!props?.airline ? 'placeholder' : props?.airline}
                  >
                    <MenuItem value='placeholder' disabled>Select an airline</MenuItem>
                    {(() => {
                      let filtered_airlines = props.airlines;
                      // if (props.combinationAirline && props.combinationAirline.length>0) {
                      //   let combinationAirlineIds = props.combinationAirline.map((combination) => combination.airlines.map((airline) => airline.id)).flat();
                      //   filtered_airlines = props.airlines.filter((airline) => !combinationAirlineIds.includes(airline.id));
                      // }
                      return filtered_airlines.map((airline, i) => (
                        <MenuItem key={i} value={airline.id}>
                          {airline.code}
                        </MenuItem>
                      ));
                    })()}
                    <MenuItem value='placeholder' disabled>Select Alliance</MenuItem>
                    {props.combinationAirline.map((combination, i) => (
                      <MenuItem key={i} value={combination.airlines.map((airline) => parseInt(airline.id)).join(',')}>
                        {combination.airlines.map((airline, j) => (
                          <span key={j}>
                            {airline.code}{j !== combination.airlines.length - 1 ? '-' : ''}
                          </span>
                        ))}
                      </MenuItem>
                    ))}
                  </CustomSelectRounded>
                </FormControl>
              </Stack>
            </Grid>
            {props.loadInfo !== 'discrepancy' ? 
              <Grid item xs={12} sm={6} md={3} lg={3.5} xl={3.5}>
                <Stack {...STACKFILTER}>
                  <CustomInputLabel id='contract_label' aria-label='Enter-contract' className={styles.ipLabel}>Department</CustomInputLabel>
                  <FormControl variant='standard' fullWidth>
                    <CustomSelectRounded
                      {...SelectInputProps}
                      id='airline'
                      labelId='contract-label'
                      fullWidth
                      autoFocus
                      className={`${styles.brInput}`}
                      onChange={props.handleContractType}
                      value={props.contract_Type} 
                    >
                      <MenuItem value='placeholder' disabled>Select a Department</MenuItem>
                      {cookies.get('contract_types')?.map((type, index) => (
                        <MenuItem key={index} value={type}>{type}</MenuItem>
                      ))}
                    </CustomSelectRounded>
                  </FormControl>
                </Stack>
              </Grid> : ''
            }

            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
              <Stack {...STACKFILTER}>
                <CustomInputLabel htmlFor='' aria-label='Enter month' className={`${styles.ipLabel}`}>Month</CustomInputLabel>
                <CustomDatePicker
                  slotProps={{
                    textField: {
                      ...InputProps,
                      placeholder: 'Enter Month',
                      fullWidth: true
                    },
                  }}
                  views={['month', 'year']}
                  className={`${styles.brInput} ${styles.w100} ${styles.minWidth125} ${styles.fOne}`}
                  openTo='month'
                  onChange={e =>
                    props.onChangeSearch({
                      target: {
                        name: 'month',
                        value: moment(e.$d).format('YYYY-MM-DD')
                      }
                    })
                  }
                  value={ props.search.from ? dayjs(props.search.from) : null }
                />
              </Stack>
            </Grid>
            {
              ['System Admin', 'Supervisor', 'Manager'].includes(cookies.get('role')) &&
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <Stack {...STACKFILTER}>
                    <CustomInputLabel htmlFor='' aria-label='Enter period' className={`${styles.ipLabel}`}>Role</CustomInputLabel>
                    <FormControl variant='standard' fullWidth>
                      <CustomSelectRounded
                        {...SelectInputProps}
                        id='role'
                        labelId='role-label'
                        fullWidth
                        name = 'role'
                        value = {props.role}
                        onChange = {props.handleRoleChange}
                      >
                        <MenuItem value='placeholder' disabled>Select Role</MenuItem>
                        <MenuItem value='accountant'>Filter by Accountant</MenuItem>
                        <MenuItem value='air_rep'>Filter by Airline Rep</MenuItem>
                      </CustomSelectRounded>
                    </FormControl>
                  </Stack>
                </Grid>
            }
          </Grid>
        </StyledCard>
      </div>

      <StyledCard className={styles.mb4}>
        <FlexDiv><StyledCardTitle>Flight Report</StyledCardTitle></FlexDiv>
        <Stack direction='row' spacing={3} justifyContent='flex-end' alignItems={'center'} mt={'-32px'} mb={2}
          divider={<Divider orientation='vertical' variant='middle' flexItem sx={{
            backgroundColor: '#ffffff8f',
            height: '30px', 
          }} />}
        >
          <Stack direction='row' spacing={2} alignItems='center'> 
            <FormLabel component='small' className={`${styles.xsW100}`}><small>Filter: </small> </FormLabel>
            <CustomCheckbox
              control={
                <Checkbox
                  checked={props.showCompleted}
                  onChange={e => props.handleShowCompleted(e.target.checked)}
                />
              }
              label='Show Processed Flights'
              className={`${styles.mr0}`}
            />
          </Stack>
          <CustomSearch
            id='search'
            variant='outlined'
            size='small'
            type='search'
            placeholder='Search Flights'
            name='search'
            className={`${styles.brInput}`}
            value={props.searchText || ''}
            onChange={props.onChangeSearchText}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              endAdornment: <InputAdornment position='end'>
                <IconButton
                  size='small'
                  aria-label='search'
                  edge='end'
                >
                  <Search fontSize='inherit' />
                </IconButton>
              </InputAdornment>,
            }}
          />
        </Stack>

        <Box className={`${styles.TStableWrapper} ${approvalStyles.FRtableWrapper} ${styles.FRtableWrapper}`} sx={{ height: 'calc(var(--contentBox) - 210px)', }}>
          <TableWrapper
            columns={col}
            data={props.list}
            isClientPagination={true}
            pageIndex={props.page}
            pageSize={props.pageSize}
          />
        </Box>
        <TablePagination
          component='div'
          count={props.list?.length || 0}
          page={props.page || 0}
          onPageChange={props.handleChangePage}
          rowsPerPage={props.pageSize || 50}
          onRowsPerPageChange={props.handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </StyledCard>

      <div>
        <CustomButton variant='contained' className={`${styles.mlauto} ${styles.dFlex} ${styles.w100} ${styles.maxWidth200}`} 
          name='approve'
          onClick={(e) => props.handleReview(e.currentTarget, null)}
        >Bulk Review</CustomButton> 
      </div>

      <ReviewModal anchorEl={props.anchorEl} handleReview={props.handleReview} 
        modalflight={props.modalflight} 
        submitFunc={props.UpdateFlightReport} 
        remark={props.remark} 
        setRemark={props.setRemark}
        selectedOption={props.selectedOption}
        selectOption={props.selectOption}
        handleClose={props.handleReviewClose}
      />

      <FlightReportModal 
        showModal={props.modal} closeModal={props.onClose} iframeRef={props.iframeRef}
        handleModalOpen={props.handleModalOpen}
        modalOpen={props.modalOpen}
        handleModalClose={props.handleModalClose}
        flight={props.flightReportData}
        ctype={props.ctype}
      />

    </div>
  );
};

export default FlightReport;
